import img0 from "@/assets/authors/christi-doporto.avif";
import img1 from "@/assets/authors/colleen-james.avif";
import img2 from "@/assets/authors/jonathan-fuentes.avif";
import img3 from "@/assets/authors/josh-hoganson.avif";
import img4 from "@/assets/authors/shawn-snyder.avif";

export interface Author {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  name: string;
  title: string;
  tags: string[];
  linkedin: string | null;
  highlights: { value: string; label: string }[];
  bio: string[];
  expertise: { title: string; body: string }[];
  certsHeading: string;
  certs: string[];
  orgsHeading: string;
  orgsIntro: string;
  orgs: string[];
  footerLine: string;
  photo: string;
}

export const authors: Author[] = [
  {
    slug: "christi-doporto",
    metaTitle: "Christi Doporto | Talent Development Consultant Coach | Leadership Edge Live",
    metaDescription:
      "Christi Doporto is a Talent Development Consultant and Executive Coach with 20+ years across Fortune 500 organizations. PHR and ACC certified, best-selling author.",
    name: "Christi Doporto MA, PHR, ACC",
    title: "Leadership Development Consultant & Executive Coach",
    tags: [
      "Talent Development",
      "Executive Coaching",
      "Organizational Development",
      "Culture and Communication",
    ],
    linkedin: "https://www.linkedin.com/in/christidoporto/",
    highlights: [
      {
        value: "Author",
        label: "Published Best-Seller",
      },
    ],
    bio: [
      "Christi Doporto is a Talent Development Consultant and Executive Coach at Leadership Edge Live with more than two decades of experience developing leaders, strengthening organizational culture, and facilitating high-impact training. Her career spans Fortune 500 environments across airline, manufacturing, defense, insurance, financial services, and transportation, where she built a reputation for blending strategic HR expertise with deeply human-centered coaching.",
      "Christi's approach is rooted in a genuine belief that every leader has untapped potential waiting to be reached. She helps organizations assess their talent, build succession pipelines, drive culture change, and develop the internal capability needed to perform at the highest level. Whether she is working one-on-one with an executive or running a workshop for a full leadership cohort, she brings the same standard: practical, measurable, and lasting impact.",
      "Outside of her professional work, Christi is a best-selling published author, plays the piano, creates floral arrangements, and travels with her family. She is also an advocate for autism-spectrum awareness and places a strong personal value on health and well-being.",
    ],
    expertise: [
      {
        title: "Executive Coaching",
        body: "One-on-one coaching that helps leaders become more self-aware, close their gaps, realize their full potential, and get promoted faster without losing their values along the way.",
      },
      {
        title: "Talent Development",
        body: "Designing and running talent development programs that assess capability, build succession pipelines, and create the internal bench strength organizations need to grow with confidence.",
      },
      {
        title: "Organizational Development",
        body: "Helping companies drive change management, build stronger cultures, and measure the overall effectiveness of their talent management initiatives against real business outcomes.",
      },
      {
        title: "Culture and Communication",
        body: "Building communication strategies and cultural frameworks that help leaders connect across levels, functions, and differences in ways that actually shift how people work together.",
      },
      {
        title: "Leadership Training Facilitation",
        body: "Facilitating high-impact workshops across in-person and virtual formats, with a style that combines strategic depth with the kind of warmth that makes people genuinely willing to engage.",
      },
      {
        title: "HR Strategy & Talent Reviews",
        body: "Partnering with senior leadership to conduct talent reviews, develop succession plans, and build the HR infrastructure that turns people strategy into competitive advantage.",
      },
    ],
    certsHeading: "Certifications & Credentials",
    certs: [
      "Professional in Human Resources (PHR)",
      "Associate Certified Coach (ACC) — ICF",
      "Franklin Covey Certified",
      "DDI Certified",
      "Ken Blanchard Certified",
      "Predictive Index Certified",
      "DISC Certified",
      "Change Management Certified",
      "Organizational Development Certified",
    ],
    orgsHeading: "Industries Christi Has Worked With",
    orgsIntro:
      "Christi's Fortune 500 experience spans a wide range of industries. She has built and led talent development and executive coaching engagements across:",
    orgs: [
      "Airline",
      "Manufacturing",
      "Defense",
      "Insurance",
      "Financial Services",
      "Transportation",
    ],
    footerLine: "Leadership Edge Live · Talent Development & Executive Coaching",
    photo: img0,
  },
  {
    slug: "colleen-james",
    metaTitle:
      "Colleen James | Leadership Coach & Employee Development Specialist | Leadership Edge Live",
    metaDescription:
      "Colleen James is a Senior Facilitator at Leadership Edge Live with over 20 years of experience in leadership coaching, employee development, and HR strategy. She has worked with Desert Diamond Casinos, Cliff Castle Casino, and Freeport McMoRan.",
    name: "Colleen James",
    title: "Senior Leadership Development Consultant",
    tags: ["Leadership Coaching", "Employee Development", "HR Strategy", "Training Facilitation"],
    linkedin: "https://www.linkedin.com/in/colleen-james-27918b6/",
    highlights: [],
    bio: [
      "Colleen James is a Senior Facilitator at Leadership Edge Live and a Learning and Development Specialist with over 20 years of experience in leadership coaching, employee development, and HR strategy. She has worked with organizations across the gaming, entertainment, and natural resources industries, including Desert Diamond Casinos and Entertainment, Cliff Castle Casino, and Freeport McMoRan. In each engagement, her focus is the same: helping people not only learn new skills but apply them in ways that genuinely change how they work and lead.",
      "Colleen believes that small shifts spark big, lasting success. That belief shapes everything about how she facilitates. She holds certifications from Franklin Covey, Crucial Learning, Predictive Index, and Crestcom BulletProof Manager, and she brings that depth of training methodology to every session she runs. What sets Colleen apart is her ability to connect with people. She has a natural gift for making participants feel comfortable, valued, and motivated to grow. She has even been known to make new employee orientation inspiring. If you know corporate training, you know how rare that is.",
    ],
    expertise: [
      {
        title: "Leadership Development",
        body: "Working one-on-one and in group settings with managers and leaders at all levels to build the self-awareness, communication skills, and confidence needed to lead effectively.",
      },
      {
        title: "Employee Development",
        body: "Designing and delivering development programs that help employees grow within their roles, improve performance, and take on greater responsibility over time.",
      },
      {
        title: "HR Strategy",
        body: "Drawing on 20+ years of experience across HR functions to help organizations align their people practices with their broader business goals and culture.",
      },
      {
        title: "Training Facilitation",
        body: "Facilitating live sessions across in-person and virtual formats with an engaging, practical style that connects methodology to real workplace situations.",
      },
      {
        title: "Onboarding & Orientation",
        body: "Bringing energy and structure to onboarding programs that are often treated as a checkbox exercise. Colleen turns them into genuine first impressions that set new hires up to succeed.",
      },
      {
        title: "Behavioral & Skills Assessment",
        body: "Certified in Predictive Index, which she uses to help organizations understand their people better and make smarter decisions about development, hiring, and team design.",
      },
    ],
    certsHeading: "Certifications & Credentials",
    certs: [
      "Franklin Covey Certified",
      "Crucial Learning Certified",
      "Predictive Index Certified",
      "Crestcom BulletProof Manager",
    ],
    orgsHeading: "Organizations Colleen Has Worked With",
    orgsIntro:
      "Colleen has delivered leadership coaching and employee development programs for organizations in the gaming, entertainment, and natural resources sectors, including:",
    orgs: ["Desert Diamond Casinos and Entertainment", "Cliff Castle Casino", "Freeport McMoRan"],
    footerLine: "Leadership Edge Live · Leadership Coaching & Employee Development",
    photo: img1,
  },
  {
    slug: "jonathan-fuentes",
    metaTitle:
      "Jonathan Fuentes | Leadership Development Facilitator & Agile Expert | Leadership Edge Live",
    metaDescription:
      "Jonathan Fuentes is a Facilitator at Leadership Edge Live. A former Microsoft product manager, he brings analytical thinking and Agile Project Management expertise to leadership development training.",
    name: "Jonathan Fuentes",
    title: "Leadership Development Consultant",
    tags: ["Leadership Development", "Agile Project Management", "Analytical Thinking"],
    linkedin: "https://www.linkedin.com/in/jonathan-a-fuentes/",
    highlights: [],
    bio: [
      "Jonathan Fuentes is a Facilitator at Leadership Edge Live with a background in product management at Microsoft. Before moving into leadership development, he worked at one of the most analytically rigorous companies in the world, where the ability to cut through complexity, prioritize what matters, and communicate clearly under pressure is not optional. Jonathan brings that same sharp, structured thinking into every facilitation session he runs, helping teams cut through noise and focus on what actually moves the needle.",
      "Participants describe Jonathan as detail-oriented and unafraid to challenge them toward growth. Outside of work, he teaches dance as a part-time instructor, which says something important about how he approaches complexity: he breaks down even the most difficult concepts, whether in business strategy or the ballroom, into steps that are clear, attainable, and rewarding. That same patience and precision is what makes him effective in the training room. He holds certifications in Agile Project Management, UX Design, and the 4P Personality Styles framework, and has delivered programs for organizations including Freeport McMoRan, Wire Experts, and DAS.",
    ],
    expertise: [
      {
        title: "Analytical Thinking & Problem Solving",
        body: "Drawing on his product management background at Microsoft to help teams identify root causes, cut through complexity, and make clearer decisions under pressure.",
      },
      {
        title: "Agile Project Management",
        body: "Certified in Agile methodologies with practical experience applying them in fast-moving, high-stakes business environments. Translates Agile principles into team behavior and leadership practice.",
      },
      {
        title: "Leadership Development Facilitation",
        body: "Delivering live leadership training that challenges participants to grow, with a structured, detail-oriented style that keeps sessions focused and outcomes measurable.",
      },
      {
        title: "UX Design & Learning Experience",
        body: "Certified in UX Design and applies design thinking principles to learning program development, creating training experiences that are intuitive, engaging, and genuinely useful.",
      },
      {
        title: "Personality Styles & Team Dynamics",
        body: "Certified in the 4P Personality Styles framework, which he uses to help teams understand how different working styles interact and turn that awareness into stronger collaboration.",
      },
      {
        title: "Simplifying Complexity",
        body: "A core strength developed across product management and dance instruction. Jonathan specializes in taking difficult concepts and breaking them into clear, sequential steps people can learn, practice, and apply.",
      },
    ],
    certsHeading: "Certifications & Credentials",
    certs: ["4P Personality Styles", "Agile Project Management", "UX Design"],
    orgsHeading: "Organizations Jonathan Has Worked With",
    orgsIntro:
      "Jonathan has delivered leadership development and training programs for organizations across multiple sectors, including:",
    orgs: ["Freeport McMoRan", "Wire Experts", "DAS"],
    footerLine: "Leadership Edge Live · Agile, UX & Analytical Leadership",
    photo: img2,
  },
  {
    slug: "josh-hoganson",
    metaTitle: "Josh Hoganson | Senior Leadership Development Facilitator | Leadership Edge Live",
    metaDescription:
      "Josh Hoganson is a Senior Facilitator at Leadership Edge Live with 25+ years in leadership development and organisational growth. He has built L&D functions for JP Morgan Chase, LoanCare, and Orion Lending.",
    name: "Josh Hoganson",
    title: "Senior Leadership Development Consultant",
    tags: [
      "Leadership Development",
      "L&D Strategy",
      "Performance Improvement",
      "Conflict Resolution",
    ],
    linkedin: "http://linkedin.com/in/joshhoganson",
    highlights: [
      {
        value: "F500",
        label: "CLIENT EXPERIENCE",
      },
    ],
    bio: [
      "Josh Hoganson is a Senior Facilitator at Leadership Edge Live and a Learning and Development executive with more than 25 years of experience in organizational growth, leadership development, and performance improvement. At JP Morgan Chase, he led strategic L&D initiatives that shaped how one of the world's largest financial institutions developed its people. He later built scalable learning and development functions from the ground up for fast-growth companies including LoanCare, Pacific Union Financial, Orion Lending, and ClearEdge Lending.",
      "Beyond his professional work, Josh is committed to giving back. He actively supports causes including children's education, cancer research, and autism awareness, and coaches adult sports leagues. That same passion for mentorship and team development drives his facilitation work. As both a musician and a storyteller, Josh brings genuine creativity and human connection to every session he leads, because he believes creativity and connection are core drivers of lasting personal and professional growth.",
    ],
    expertise: [
      {
        title: "Leadership Development",
        body: "Facilitating programs for managers, directors, and senior leaders across financial services and enterprise organizations.",
      },
      {
        title: "L&D Strategy",
        body: "Building learning architectures for organizations at scale, from strategic design through full program deployment and measurement.",
      },
      {
        title: "Organizational Growth",
        body: "Supporting fast-growth companies in building the internal people capability needed to scale without losing performance or culture.",
      },
      {
        title: "Performance Improvement",
        body: "Identifying performance gaps and designing targeted interventions that produce measurable improvement at the team and organizational level.",
      },
      {
        title: "Conflict Resolution & Negotiation",
        body: "Facilitating sessions in conflict resolution and win-win negotiation that give participants practical tools they can use immediately.",
      },
      {
        title: "Mentorship & Team Development",
        body: "Drawing on decades of professional and community coaching experience to develop people who lead with confidence.",
      },
    ],
    certsHeading: "Certifications & Credentials",
    certs: [
      "Crucial Learning Certified",
      "Behavioral Interviewing & Conflict Resolution",
      "Win-Win Negotiations",
    ],
    orgsHeading: "Organizations Josh Has Worked With",
    orgsIntro:
      "Josh has built and led L&D functions and delivered leadership programs for organizations across financial services and enterprise sectors, including:",
    orgs: [
      "JP Morgan Chase",
      "Pacific Union Financial",
      "LoanCare",
      "Orion Lending",
      "ClearEdge Lending",
    ],
    footerLine: "Leadership Edge Live · Leadership Development",
    photo: img3,
  },
  {
    slug: "shawn-snyder",
    metaTitle:
      "Shawn Snyder | Leadership Development Expert & Executive Coach | Leadership Edge Live",
    metaDescription:
      "Shawn Snyder is the Founder of Leadership Edge Live with 25+ years in leadership development. He has led multi-million dollar change initiatives for Pfizer, Deloitte, and the U.S. Department of Defense",
    name: "Shawn Snyder",
    title: "Founder & Chief Learning Strategist",
    tags: [
      "Leadership Development",
      "Executive Coaching",
      "Change Management",
      "Instructional Design",
    ],
    linkedin: "http://linkedin.com/in/shawnssnyder",
    highlights: [
      {
        value: "Author",
        label: "Published Best-Seller",
      },
    ],
    bio: [
      "Shawn Snyder is the Founder of Leadership Edge Live and one of the most experienced leadership development practitioners working in corporate training today. Over 25 years, he has designed and led change initiatives for some of the most recognized organizations in the world, including Pfizer, Deloitte, the State of Arizona, the U.S. Department of Defense, and Amway. His work has delivered measurable outcomes: higher sales performance, improved employee morale, and significantly reduced turnover across industries and geographies.",
      "Shawn holds four professional certifications and is an expert in instructional design. He has built and deployed technology tools that improve learner engagement in live training environments, and his approach adapts to different cultural and organizational dynamics. That is why multinational clients trust him to run programs across borders.",
      "In the room, Shawn brings genuine energy and humor to sessions that many trainers make unnecessarily dry. He is known for winning over even resistant participants and leaving people with not just new skills but the confidence to actually use them. He has trained and spoken to leaders on every continent, and that global perspective shapes how he designs every program at Leadership Edge Live.",
    ],
    expertise: [
      {
        title: "Leadership Development",
        body: "Designing and delivering programs for individual contributors through to senior executives across Fortune 500 companies and government agencies.",
      },
      {
        title: "Executive Coaching",
        body: "One-on-one and group coaching focused on behavioral change, performance improvement, and long-term leadership effectiveness.",
      },
      {
        title: "Change Management",
        body: "Prosci ADKAR-certified practitioner with direct experience leading enterprise-wide change programs for large, complex organizations.",
      },
      {
        title: "Instructional Design",
        body: "Building scalable training architecture across in-person, virtual, and hybrid formats, with technology integration for deeper learner engagement.",
      },
      {
        title: "Human Performance Improvement",
        body: "Diagnosing root causes of performance gaps and designing interventions that produce lasting behavior change at the individual and organizational level.",
      },
      {
        title: "Organisational Development",
        body: "Building internal capability through structured learning strategies, culture initiatives, and leadership pipeline development for complex organizations.",
      },
    ],
    certsHeading: "Certifications & Credentials",
    certs: [
      "Certified Corporate Coach",
      "Prosci ADKAR Change Management Practitioner",
      "Korn Ferry Leadership Architect",
      "LEGO Serious Play Facilitator",
    ],
    orgsHeading: "Organizations Shawn Has Worked With",
    orgsIntro:
      "Shawn has delivered leadership development and change management programs for a wide range of organizations across the private sector, government, and non-profit industries.",
    orgs: ["Pfizer", "Deloitte", "State of Arizona", "U.S. Department of Defense", "Amway"],
    footerLine: "Leadership Edge Live · Leadership Development",
    photo: img4,
  },
];

export const authorBySlug = (slug: string) => authors.find((a) => a.slug === slug);

/** Match a blog post's author display name to an author page. */
export const authorByName = (name: string) =>
  authors.find((a) =>
    name.toLowerCase().startsWith(a.name.split(" ").slice(0, 2).join(" ").toLowerCase()),
  );
